/**
 * Account Details Drawer
 *
 * Displays linked account information for a consent request.
 * Shows accounts grouped by FIP ID with basic metadata.
 * This drawer works independently of FI data availability - it shows
 * account metadata as soon as consent is approved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
} from 'equal-ds-ui';
import {
  Building2,
  CreditCard,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Copy,
  Check,
  X,
} from 'lucide-react';
import { consentRequestAPI } from '../../../services/consent-request-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import type {
  ConsentAccountRecord,
  ConsentAccountGroup,
  AccountDetailsDrawerProps,
} from '../../../types/consent';

/**
 * Copy Button with larger icon for better visibility
 * Shows success (checkmark) or failure (X) feedback
 */
interface CopyIconButtonProps {
  text: string;
}

type CopyState = 'idle' | 'copied' | 'failed';

const CopyIconButton: React.FC<CopyIconButtonProps> = ({ text }) => {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setCopyState('idle');
      }, 2000);
    } catch {
      // Clipboard access denied or not available - show error feedback
      setCopyState('failed');

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setCopyState('idle');
      }, 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
      title={copyState === 'failed' ? 'Copy failed' : 'Click to copy'}
    >
      {copyState === 'copied' ? (
        <Check className="w-4 h-4 text-green-600" />
      ) : copyState === 'failed' ? (
        <X className="w-4 h-4 text-red-500" />
      ) : (
        <Copy className="w-4 h-4 text-gray-700 hover:text-gray-900" />
      )}
    </button>
  );
};

/**
 * Groups accounts by FIP ID for organized display
 */
const groupAccountsByFip = (accounts: ConsentAccountRecord[]): ConsentAccountGroup[] => {
  const map = new Map<string, ConsentAccountGroup>();
  accounts.forEach((account) => {
    const fipId = account.fip_id ?? 'UNKNOWN';
    if (!map.has(fipId)) {
      map.set(fipId, { fip_id: fipId, accounts: [] });
    }
    map.get(fipId)?.accounts.push(account);
  });
  return Array.from(map.values());
};

/**
 * Get icon for account type
 */
const getAccountTypeIcon = (fiType?: string | null) => {
  const type = (fiType ?? '').toUpperCase();
  switch (type) {
    case 'DEPOSIT':
    case 'SAVINGS':
    case 'CURRENT':
      return <CreditCard className="w-4 h-4 text-blue-500" />;
    case 'MUTUAL_FUNDS':
    case 'EQUITIES':
    case 'ETF':
      return <Building2 className="w-4 h-4 text-green-500" />;
    case 'INSURANCE':
    case 'LIFE_INSURANCE':
    case 'GENERAL_INSURANCE':
      return <Building2 className="w-4 h-4 text-purple-500" />;
    default:
      return <CreditCard className="w-4 h-4 text-gray-500" />;
  }
};

export const AccountDetailsDrawer: React.FC<AccountDetailsDrawerProps> = ({
  isOpen,
  onClose,
  consent,
}) => {
  const { handleError } = useErrorHandler();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountGroups, setAccountGroups] = useState<ConsentAccountGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const customerVua = consent?.customer_vua ?? consent?.account_id ?? '';
  const consentId = consent?.consent_id ?? '';

  // Toggle group expansion
  const toggleGroup = useCallback((fipId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(fipId)) {
        next.delete(fipId);
      } else {
        next.add(fipId);
      }
      return next;
    });
  }, []);

  // Fetch account details when drawer opens
  useEffect(() => {
    if (!isOpen || !consentId) {
      return;
    }

    const fetchAccountDetails = async () => {
      setLoading(true);
      setError(null);
      setAccountGroups([]);

      try {
        const response = await consentRequestAPI.getConsentAccountDetails({ consentId });
        const rows = response.data.artefactAccounts?.rows ?? [];
        const groups = groupAccountsByFip(rows);
        setAccountGroups(groups);

        // Expand all groups by default
        const allFipIds = new Set(groups.map((g) => g.fip_id));
        setExpandedGroups(allFipIds);
      } catch (err) {
        handleError(err, { component: 'AccountDetailsDrawer', action: 'fetchAccountDetails' });
        setError('Failed to fetch account details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    void fetchAccountDetails();
  }, [isOpen, consentId]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setAccountGroups([]);
      setExpandedGroups(new Set());
      setError(null);
    }
  }, [isOpen]);

  const totalAccounts = accountGroups.reduce((sum, group) => sum + group.accounts.length, 0);

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="650px"
      >
        <DrawerHeader onClose={onClose}>
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold text-text-primary">Account Details</span>
            {customerVua && (
              <span className="text-sm text-text-secondary">
                For: <span className="font-medium">{customerVua}</span>
              </span>
            )}
          </div>
        </DrawerHeader>

        <DrawerContent className="p-4 overflow-y-auto">
          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-text-secondary">
              <Loader2 className="w-8 h-8 animate-spin mb-3" />
              <span>Loading account details...</span>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-12 text-red-500">
              <AlertCircle className="w-8 h-8 mb-3" />
              <span className="text-center">{error}</span>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && accountGroups.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-text-secondary">
              <CreditCard className="w-12 h-12 mb-3 opacity-50" />
              <span className="text-center">No accounts found for this consent.</span>
            </div>
          )}

          {/* Account groups */}
          {!loading && !error && accountGroups.length > 0 && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-bg-secondary rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-text-secondary">Total Linked Accounts</span>
                <span className="text-lg font-semibold text-text-primary">{totalAccounts}</span>
              </div>

              {/* Account groups by FIP */}
              {accountGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.fip_id);

                return (
                  <div
                    key={group.fip_id}
                    className="border border-border-primary rounded-lg overflow-hidden"
                  >
                    {/* Group header */}
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.fip_id)}
                      className="w-full flex items-center justify-between p-3 bg-bg-secondary hover:bg-bg-tertiary transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-text-secondary" />
                        <span className="font-medium text-text-primary">{group.fip_id}</span>
                        <span className="text-sm text-text-secondary">
                          ({group.accounts.length} account{group.accounts.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-text-secondary" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-text-secondary" />
                      )}
                    </button>

                    {/* Account list */}
                    {isExpanded && (
                      <div className="divide-y divide-border-primary">
                        {group.accounts.map((account, index) => (
                          <div
                            key={account.link_reference_number ?? `account-${index}`}
                            className="p-4 bg-bg-primary"
                          >
                            {/* Account header with masked number */}
                            <div className="flex items-center gap-2 mb-3">
                              {getAccountTypeIcon(account.fi_type)}
                              <span className="font-medium text-text-primary">
                                {account.masked_account_number ?? 'N/A'}
                              </span>
                            </div>

                            {/* Account details grid */}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-text-secondary block">Account Type</span>
                                <span className="text-text-primary font-medium">
                                  {account.account_type ?? 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-text-secondary block">FI Type</span>
                                <span className="text-text-primary font-medium">
                                  {account.fi_type ?? 'N/A'}
                                </span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-text-secondary block">Link Reference Number</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-text-primary font-medium break-all text-xs">
                                    {account.link_reference_number ?? 'N/A'}
                                  </span>
                                  {account.link_reference_number && (
                                    <CopyIconButton text={account.link_reference_number} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};
